import React, { useEffect, useState } from "react";

import MainRouterHandler from "./routers";

import {

  useMsal,

  AuthenticatedTemplate,

  UnauthenticatedTemplate,

} from "@azure/msal-react";

import Login from "./components/login/login";

import { getAdminDetails } from "Samarth Module/api/roleBaseApi";

import {

  sendEnvironmentInfo,

  getCurrentEnvironment,

  sendLoginInfo,

  setupLogoutHandler,

} from "./utils/iframeCommunication";

import { UserAccessProvider } from "utils/userAccessContext";

import { isSessionExpiredError } from "./utils/getValidToken";

const PERMISSIONS_TIMEOUT_MS = 20000;

const toUserInfo = (payload) => {

  const ui = payload?.user_info || {};

  return {

    accessType: ui.accessType,

    email: ui.email,

    id: ui.id,

    name: ui.name,

    persona: ui.persona,

    scope: ui.scope,

    scope_value: ui.scope_value,

    userType: ui.userType,

  };

};

const App = () => {

  const { accounts, instance, inProgress } = useMsal();

  const activeAccount = accounts[0] || null;

  const [userInfo, setUserInfo] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {

    // MSAL reports no account while it is still starting. Waiting avoids

    // treating that gap as a logout and forcing a blocking permissions call.

    if (inProgress && inProgress !== "none") {

      return;

    }

    if (!activeAccount) {

      setIsLoading(false);

      return;

    }

    instance.setActiveAccount(activeAccount);

    const currentSessionId = sessionStorage.getItem("sessionId");

    const cachedUserInfo = localStorage.getItem("UserInfo");

    let hasCache = false;

    if (cachedUserInfo) {

      try {

        setUserInfo(toUserInfo(JSON.parse(cachedUserInfo)));

        hasCache = true;

        setIsLoading(false);

      } catch (error) {

        console.error("Error parsing cached user info:", error);

        localStorage.removeItem("UserInfo");

      }

    }

    // Refresh once per browser tab. Cached permissions stay on screen so a

    // slow or expired token cannot leave the page on the loading message.

    if (!currentSessionId || !hasCache) {

      sessionStorage.setItem("sessionId", Date.now().toString());

      getAdminApiDetails({ blockUi: !hasCache });

    }

    sendLoginInfo(activeAccount, {

      environment: getCurrentEnvironment(),

    });

  }, [activeAccount, inProgress]);

  useEffect(() => {

    const environment = getCurrentEnvironment();

    sendEnvironmentInfo(environment, {

      appVersion: process.env.REACT_APP_VERSION || "1.0.0",

      buildDate: process.env.REACT_APP_BUILD_DATE || new Date().toISOString(),

    });

    setupLogoutHandler(() => {

      // Clear user data on logout

      localStorage.removeItem("UserInfo");

      sessionStorage.removeItem("sessionId");

      setUserInfo(null);

      setIsLoading(false);

      instance.logout();

    });

  }, [instance]);

  const getAdminApiDetails = async ({ blockUi = true } = {}) => {

    let timeoutId;

    let sessionExpired = false;

    const timeout = new Promise((_, reject) => {

      timeoutId = setTimeout(

        () => reject(new Error("Permissions request timed out")),

        PERMISSIONS_TIMEOUT_MS

      );

    });

    try {

      if (blockUi) {

        setIsLoading(true);

      }

      const response = await Promise.race([getAdminDetails(), timeout]);

      if (response?.data) {

        localStorage.setItem("UserInfo", JSON.stringify(response.data));

        setUserInfo(toUserInfo(response.data));

      } else if (blockUi) {

        localStorage.removeItem("UserInfo");

        setUserInfo({ accessType: "Read-Only" });

      }

    } catch (error) {

      console.error("Admin API error:", error);

      if (isSessionExpiredError(error)) {

        sessionExpired = true;

        setIsSigningIn(true);

      } else if (blockUi) {

        localStorage.removeItem("UserInfo");

        setUserInfo({ accessType: "Read-Only" });

      }

    } finally {

      clearTimeout(timeoutId);

      if (!sessionExpired) {

        setIsLoading(false);

      }

    }

  };

  useEffect(() => {

    const storedUserInfo = localStorage.getItem("UserInfo");

    console.log("Stored User Info:", storedUserInfo);

    if (storedUserInfo && !userInfo && !activeAccount) {

      try {

        setUserInfo(toUserInfo(JSON.parse(storedUserInfo)));

        setIsLoading(false);

      } catch (error) {

        console.error("Error parsing stored user info:", error);

        localStorage.removeItem("UserInfo");

        setIsLoading(false);

      }

    } else if (!storedUserInfo && !activeAccount) {

      setIsLoading(false);

    }

  }, [activeAccount]);

  const renderAuthenticatedContent = () => {

    if (!activeAccount) {

      return <Login />;

    }

    if (isSigningIn) {

      return (

        <div

          style={{

            display: "flex",

            justifyContent: "center",

            alignItems: "center",

            height: "100vh",

          }}

        >

          Signing you in...

        </div>

      );

    }

    if (isLoading) {

      return (
        <div

          style={{

            display: "flex",

            justifyContent: "center",

            alignItems: "center",

            height: "100vh",

          }}
        >

          Loading user permissions...
        </div>

      );

    }

    if (userInfo) {

      return (
        <UserAccessProvider>
          <MainRouterHandler />
        </UserAccessProvider>

      );

    }

    return <Login />;

  };

  return (
    <div>
      <AuthenticatedTemplate>

        {renderAuthenticatedContent()}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </div>

  );

};

export default App;

